import { api } from './api';
import type {
  WebhookListResponse,
  WebhookResponse,
  WebhookDeliveriesResponse,
  WebhookDeliveryResponse,
  WebhookEventsResponse,
  WebhookTestResponse,
  WebhookRotateSecretResponse,
  CreateWebhookRequest,
  UpdateWebhookRequest,
} from '../types/webhook';

export const webhookApi = {
  // List all webhooks for a project
  list: (projectId: string) =>
    api.get<WebhookListResponse>(`/api/projects/${projectId}/webhooks`),

  // Get a single webhook
  get: (projectId: string, webhookId: string) =>
    api.get<WebhookResponse>(`/api/projects/${projectId}/webhooks/${webhookId}`),

  // Create a new webhook
  create: (projectId: string, data: CreateWebhookRequest) =>
    api.post<WebhookResponse>(`/api/projects/${projectId}/webhooks`, data),

  // Update a webhook
  update: (projectId: string, webhookId: string, data: UpdateWebhookRequest) =>
    api.put<WebhookResponse>(`/api/projects/${projectId}/webhooks/${webhookId}`, data),

  // Delete a webhook
  delete: (projectId: string, webhookId: string) =>
    api.delete<{ success: boolean; message?: string }>(`/api/projects/${projectId}/webhooks/${webhookId}`),

  // Send a test webhook
  test: (projectId: string, webhookId: string) =>
    api.post<WebhookTestResponse>(`/api/projects/${projectId}/webhooks/${webhookId}/test`),

  // Rotate webhook signing secret
  rotateSecret: (projectId: string, webhookId: string) =>
    api.post<WebhookRotateSecretResponse>(`/api/projects/${projectId}/webhooks/${webhookId}/rotate-secret`),

  // Get webhook deliveries
  deliveries: (projectId: string, webhookId: string, params?: { limit?: number; offset?: number; status?: string; event_type?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());
    if (params?.status) searchParams.set('status', params.status);
    if (params?.event_type) searchParams.set('event_type', params.event_type);
    const queryString = searchParams.toString();
    return api.get<WebhookDeliveriesResponse>(
      `/api/projects/${projectId}/webhooks/${webhookId}/deliveries${queryString ? `?${queryString}` : ''}`
    );
  },

  // Get a single delivery
  delivery: (projectId: string, webhookId: string, deliveryId: string) =>
    api.get<WebhookDeliveryResponse>(`/api/projects/${projectId}/webhooks/${webhookId}/deliveries/${deliveryId}`),

  // Retry a failed delivery
  retryDelivery: (projectId: string, webhookId: string, deliveryId: string) =>
    api.post<{ success: boolean; message?: string }>(
      `/api/projects/${projectId}/webhooks/${webhookId}/deliveries/${deliveryId}/retry`
    ),

  // Get available webhook events
  events: (projectId: string) =>
    api.get<WebhookEventsResponse>(`/api/projects/${projectId}/webhooks/events`),
};
